import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { useAuth } from "../context/AuthContext";
import { validateTicket } from "../services/ticketing";
import Navbar from "../components/Navbar";

const READER_ID = "gvs-qr-reader";

export default function ScanPage({ cartCount, onCartClick }) {
  const { isAdmin, isEventStaff, loading } = useAuth();
  const navigate = useNavigate();

  const scannerRef   = useRef(null);
  const busyRef       = useRef(false); // guards against re-firing while a scan is being validated
  const [cameraError, setCameraError] = useState("");
  const [result,       setResult]     = useState(null); // { ok, title, subtitle }

  const canScan = !loading && (isAdmin || isEventStaff);

  useEffect(() => {
    if (!canScan) return;

    const scanner = new Html5Qrcode(READER_ID);
    scannerRef.current = scanner;

    const onDecoded = async (decodedText) => {
      if (busyRef.current) return;
      busyRef.current = true;

      let payload;
      try {
        payload = JSON.parse(decodedText);
      } catch {
        setResult({ ok: false, title: "Not a valid ticket QR code", subtitle: "" });
        setTimeout(() => { busyRef.current = false; }, 1500);
        return;
      }

      const res = await validateTicket({ ticketId: payload.ticketId, code: payload.code });
      if (res.success) {
        setResult({
          ok: true,
          title: `✓ ${res.buyerName || "Guest"}`,
          subtitle: `${res.eventName || ""} · ${res.ticketTypeName || ""}`,
        });
      } else {
        setResult({ ok: false, title: "✕ Invalid ticket", subtitle: res.error || "" });
      }
      setTimeout(() => { busyRef.current = false; setResult(null); }, 2500);
    };

    // start() is async (camera permission + init) — if the component unmounts
    // before it resolves (e.g. React StrictMode's mount→cleanup→mount in dev),
    // calling stop() too early throws "scanner is not running or paused".
    // Track whether it actually started, and stop late if we unmounted mid-start.
    let isRunning  = false;
    let unmounted  = false;

    const config = { fps: 10, qrbox: 250 };

    const describeError = (err) => {
      const name = err?.name || "";
      if (!window.isSecureContext) {
        return "Camera access requires HTTPS. This page isn't loaded over a secure connection.";
      }
      if (name === "NotAllowedError") {
        return "Camera permission was denied. Allow camera access for this site in your browser settings, then reload.";
      }
      if (name === "NotFoundError" || name === "OverconstrainedError") {
        return "No usable camera was found on this device.";
      }
      if (name === "NotReadableError") {
        return "The camera is already in use by another app or browser tab.";
      }
      return err?.message || String(err) || "Could not access camera.";
    };

    // Prefer the back camera; fall back to any camera if that exact
    // constraint isn't satisfiable (e.g. laptops with only a front webcam).
    scanner.start({ facingMode: "environment" }, config, onDecoded, () => {})
      .catch(() => scanner.start({ facingMode: "user" }, config, onDecoded, () => {}))
      .then(() => {
        isRunning = true;
        if (unmounted) scanner.stop().then(() => scanner.clear()).catch(() => {});
      })
      .catch((err) => {
        console.error("Camera start failed:", err);
        if (!unmounted) setCameraError(describeError(err));
      });

    return () => {
      unmounted = true;
      if (isRunning) scanner.stop().then(() => scanner.clear()).catch(() => {});
    };
  }, [canScan]);

  useEffect(() => {
    if (!loading && !isAdmin && !isEventStaff) navigate("/");
  }, [loading, isAdmin, isEventStaff, navigate]);

  if (loading || (!isAdmin && !isEventStaff)) return null;

  return (
    <div style={{ minHeight: "100vh", background: "#faf9f7" }}>
      <Navbar cartCount={cartCount || 0} onCartClick={onCartClick || (() => {})} />

      <div style={{ maxWidth: 480, margin: "0 auto", padding: "32px 20px", textAlign: "center" }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, color: "#1a1a1a" }}>
          Scan Tickets
        </h2>
        <p style={{ fontSize: 12, color: "#aaa", marginBottom: 20 }}>
          Point the camera at a guest's ticket QR code.
        </p>

        <div id={READER_ID} style={{ borderRadius: 12, overflow: "hidden", background: "#000" }} />

        {cameraError && (
          <p style={{ fontSize: 12, color: "#c0392b", marginTop: 16, lineHeight: 1.6 }}>
            {cameraError}
          </p>
        )}

        {result && (
          <div style={{
            marginTop: 20, borderRadius: 12, padding: "18px 20px",
            background: result.ok ? "#e8f5e9" : "#fde8e8",
            color:      result.ok ? "#2e7d32" : "#c0392b",
          }}>
            <p style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px" }}>{result.title}</p>
            {result.subtitle && <p style={{ fontSize: 12, margin: 0 }}>{result.subtitle}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
